/**
 * TipTap extensions for the form document editor.
 *
 * Every custom node serializes to the exact legacy HTML contract documented
 * in ../formPlaceholders.js — the public signing page and the Cloud Functions
 * substitute these blocks with regexes keyed on class names and attribute
 * order. Attribute order in renderHTML matters (class first).
 */

import { Node, Mark } from "@tiptap/core";
import { Table } from "@tiptap/extension-table";
import { ReactNodeViewRenderer } from "@tiptap/react";
import { PATIENT_FIELD_BY_KEY } from "../formPlaceholders";
import { SignatureSlotView, TherapistSignatureView, PageBreakView } from "./FormNodeViews";

/* ---------------- Client signature slot (block atom) ---------------- */
export const SignatureSlot = Node.create({
  name: "signatureSlot",
  group: "block",
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      slotId: {
        default: "1",
        parseHTML: (element) => element.getAttribute("data-signature-id") || "1",
      },
    };
  },

  parseHTML() {
    return [{ tag: "div.signature-placeholder" }];
  },

  renderHTML({ node }) {
    const slotId = String(node.attrs.slotId || "1");
    return [
      "div",
      { class: "signature-placeholder", "data-signature-id": slotId },
      ["span", { class: "signature-line" }],
      ["span", { class: "signature-label" }, `חתימה ${slotId}`],
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(SignatureSlotView);
  },
});

/* ---------------- Therapist signature (block atom, image) ---------------- */
export const TherapistSignature = Node.create({
  name: "therapistSignature",
  group: "block",
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      src: {
        default: "",
        parseHTML: (element) => {
          const img = element.querySelector("img");
          return img ? img.getAttribute("src") || "" : "";
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: "div.therapist-signature-block" }];
  },

  renderHTML({ node }) {
    return [
      "div",
      { class: "therapist-signature-block" },
      ["img", { src: node.attrs.src, class: "therapist-signature-img", alt: "חתימת מטפל" }],
      ["span", { class: "therapist-signature-label" }, "חתימת מטפל"],
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(TherapistSignatureView);
  },
});

/* ---------------- Page break (block atom) ---------------- */
export const PageBreak = Node.create({
  name: "pageBreak",
  group: "block",
  atom: true,
  draggable: true,
  selectable: true,

  parseHTML() {
    return [{ tag: "div.page-break" }];
  },

  renderHTML() {
    return ["div", { class: "page-break" }];
  },

  addNodeView() {
    return ReactNodeViewRenderer(PageBreakView);
  },
});

/* ---------------- Patient field (inline atom) ---------------- */
export const PatientField = Node.create({
  name: "patientField",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      field: {
        default: "fullName",
        parseHTML: (element) => element.getAttribute("data-field") || "fullName",
      },
      label: {
        default: null,
        parseHTML: (element) => element.textContent || null,
      },
    };
  },

  parseHTML() {
    return [{ tag: "span.patient-field-placeholder" }];
  },

  renderHTML({ node }) {
    const field = node.attrs.field;
    const catalogue = PATIENT_FIELD_BY_KEY[field];
    const label = node.attrs.label || (catalogue ? catalogue.placeholder : `{${field}}`);
    return ["span", { class: "patient-field-placeholder", "data-field": field }, label];
  },
});

/* ---------------- Free text field for the client (inline atom) ---------------- */
export const FreeTextField = Node.create({
  name: "freeTextField",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      label: {
        default: "טקסט",
        parseHTML: (element) => element.getAttribute("data-label") || "טקסט",
      },
    };
  },

  parseHTML() {
    return [{ tag: "span.free-text-placeholder" }];
  },

  renderHTML({ node }) {
    const label = String(node.attrs.label || "טקסט");
    return [
      "span",
      { class: "free-text-placeholder", "data-label": label },
      `[טקסט חופשי: ${label}]`,
    ];
  },
});

/* ---------------- Signature date (inline atom) ---------------- */
export const SignatureDate = Node.create({
  name: "signatureDate",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  parseHTML() {
    return [{ tag: "span.signature-date-placeholder" }];
  },

  renderHTML() {
    return ["span", { class: "signature-date-placeholder" }, "[תאריך חתימה]"];
  },
});

/* ---------------- Legacy text-size spans (mark, preserved as-is) ---------------- */
export const LegacyTextSize = Mark.create({
  name: "legacyTextSize",

  addAttributes() {
    return {
      size: {
        default: "normal",
        parseHTML: (element) => {
          if (element.classList.contains("text-size-heading")) return "heading";
          if (element.classList.contains("text-size-subheading")) return "subheading";
          return "normal";
        },
      },
    };
  },

  parseHTML() {
    return [
      { tag: "span.text-size-heading" },
      { tag: "span.text-size-subheading" },
      { tag: "span.text-size-normal" },
    ];
  },

  renderHTML({ mark }) {
    return ["span", { class: `text-size-${mark.attrs.size || "normal"}` }, 0];
  },
});

/* ---------------- Table with "layout" mode (borderless column split) ---------------- */
export const FormTable = Table.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      layout: {
        default: false,
        parseHTML: (element) => element.classList.contains("ck-layout"),
        renderHTML: (attributes) => (attributes.layout ? { class: "ck-layout" } : {}),
      },
    };
  },
});

/**
 * ProseMirror JSON for a borderless N-column layout block (one row).
 */
export const buildLayoutTable = (columns) => ({
  type: "table",
  attrs: { layout: true },
  content: [
    {
      type: "tableRow",
      content: Array.from({ length: columns }, () => ({
        type: "tableCell",
        content: [{ type: "paragraph" }],
      })),
    },
  ],
});

export const FORM_CUSTOM_NODE_NAMES = [
  "signatureSlot",
  "therapistSignature",
  "pageBreak",
  "patientField",
  "freeTextField",
  "signatureDate",
];
